import React, { useCallback, useMemo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { AUDIT_CHANNEL_LABELS } from "@common/constants/agent-channel";
import { AuditChannel } from "@common/types/auditLog";
import type { ActivityFilterRows } from "@components/activityTimeline/filters/activityFilters.types";
import type { HistoryChangeFilters } from "@hooks/view/useHistoryPageFiltersCache";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    excludeDisabledChannels,
    isHistoryChannelEnabled,
} from "./historyActivityFilters";

interface Props {
    filters: HistoryChangeFilters;
    onChange: (filters: HistoryChangeFilters) => void;
}

/** Updated through: the channels this workspace can see, Agent only with agent runs. */
export function useChannelFilterRows({
    filters,
    onChange,
}: Props): ActivityFilterRows<AuditChannel> {
    const { agentRuns: isAgentRunsEnabled } = useTypedFlags();
    const channelItems = useMemo<Array<PickerItemData<AuditChannel>>>(
        () =>
            Object.values(AuditChannel)
                .filter((channel) =>
                    isHistoryChannelEnabled(channel, { isAgentRunsEnabled }),
                )
                .map((channel) => ({
                    value: channel,
                    label: AUDIT_CHANNEL_LABELS[channel],
                })),
        [isAgentRunsEnabled],
    );
    const selectedChannels = useMemo(
        () => excludeDisabledChannels(filters.channels, { isAgentRunsEnabled }),
        [filters.channels, isAgentRunsEnabled],
    );
    const selectedCount = selectedChannels.length;

    const triggerLabel = useMemo(() => {
        if (selectedCount === 0) {
            return "Updated through";
        }
        if (selectedCount === 1) {
            return AUDIT_CHANNEL_LABELS[selectedChannels[0]];
        }
        return `${selectedCount} channels`;
    }, [selectedCount, selectedChannels]);

    const applySelection = useCallback(
        (channels: AuditChannel[]) => {
            onChange({ channels: channels.length > 0 ? channels : undefined });
        },
        [onChange],
    );

    const resetFilter = useCallback(() => {
        onChange({ channels: undefined });
    }, [onChange]);

    return {
        list: { items: channelItems },
        value: selectedChannels,
        onValueChange: applySelection,
        onClear: resetFilter,
        chipLabel: selectedCount > 0 ? triggerLabel : undefined,
    };
}

export const ChannelFilterCombobox: React.FC<Props> = (props) => {
    const rows = useChannelFilterRows(props);
    const selectedCount = rows.value.length;

    return (
        <Picker.Select
            {...rows.list}
            multiple
            value={rows.value}
            onValueChange={rows.onValueChange}
            placeholder="Updated through"
            renderValue={() => rows.chipLabel ?? "Updated through"}
        >
            {selectedCount > 0 && (
                <Picker.Footer>
                    <FilterActionsFooter
                        deselectFunction={rows.onClear}
                        selectedCount={selectedCount}
                        hideBorderAndPadding
                    />
                </Picker.Footer>
            )}
        </Picker.Select>
    );
};
